import React from 'react';
import { Flex, Tag, Text, Button } from '@chakra-ui/react';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { PriorityBadge } from '../../../components/priority-badge/PriorityBadge';
import { StatusBadge, QueueStatus } from '../../../components/status-badge/StatusBadge';
import { QueueStat } from '../../../components/queue-stat/QueueStat';
import { AtRiskTier, ManagerHomeGrader, ManagerHomeSection } from './types';

const SECTION_PRIMARY_CTA: Record<ManagerHomeSection, string> = {
  needs_assignment: 'Assign Coders',
  in_flight: 'Open Worklist',
  coder_complete: 'Audit Cases',
  completed: 'View Project',
};

const RISK_TIER_TO_STATUS: Partial<Record<AtRiskTier, QueueStatus>> = {
  overdue: 'overdue',
  'due-soon': 'due-soon',
};

const RISK_TIER_STAT_COLOR: Record<string, string> = {
  overdue: '#c83729',
  'due-soon': '#c98a14',
};

const MUTED_STAT_COLOR = '#b0ccd6';

interface StatDef {
  value: string | number;
  label: string;
  colorOverride?: string;
}

function buildStats(
  section: ManagerHomeSection,
  totalCount: number | null,
  completeCount: number | null,
  approvedCount: number | null,
  daysFromEnd: number | null,
  atRiskTier: AtRiskTier,
): StatDef[] {
  const done = (completeCount ?? 0) + (approvedCount ?? 0);
  const pct = totalCount && totalCount > 0 ? Math.round((done / totalCount) * 100) : 0;
  const absDays = daysFromEnd !== null ? Math.abs(daysFromEnd) : null;
  const daysColor = RISK_TIER_STAT_COLOR[atRiskTier];
  const daysLabel = daysFromEnd !== null && daysFromEnd < 0 ? 'Past Due' : 'Until Due';
  const daysValue = absDays !== null ? `${absDays}d` : '-';

  if (section === 'needs_assignment') {
    return [
      { value: totalCount ?? '-', label: 'Cases Planned' },
      { value: daysValue, label: daysLabel, colorOverride: daysColor },
    ];
  }

  if (section === 'in_flight') {
    const casesValue = totalCount !== null ? `${done}/${totalCount}` : `${done}`;
    const isDoneZero = done === 0;
    return [
      { value: casesValue, label: 'Cases Coded', colorOverride: isDoneZero ? MUTED_STAT_COLOR : undefined },
      { value: `${pct}%`, label: 'Complete', colorOverride: isDoneZero ? MUTED_STAT_COLOR : undefined },
      { value: daysValue, label: daysLabel, colorOverride: daysColor },
    ];
  }

  if (section === 'completed') {
    // closedCount isn't in the ManagerHome query (only the mark-complete mutation returns it),
    // so derive the finished project's outcome tiles from the query data.
    const casesValue = totalCount !== null ? `${done}/${totalCount}` : `${done}`;
    return [
      { value: casesValue, label: 'Cases Coded' },
      { value: approvedCount ?? '-', label: 'Cases Audited' },
      { value: `${pct}%`, label: 'Final' },
    ];
  }

  // coder_complete / "For Review"
  return [
    { value: completeCount ?? '-', label: 'Awaiting Audit' },
    { value: approvedCount ?? '-', label: 'Audited', colorOverride: MUTED_STAT_COLOR },
    { value: daysValue, label: daysLabel, colorOverride: daysColor },
  ];
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatCardDate(isoDate: string | null): string | null {
  if (!isoDate) return null;
  const [yearStr, monthStr, dayStr] = isoDate.slice(0, 10).split('-');
  return `${MONTHS[parseInt(monthStr, 10) - 1]} ${parseInt(dayStr, 10)}, ${yearStr}`;
}

export interface ProjectCardProps {
  rank: number;
  projectName: string;
  groupLabel: string;
  purpose: string;
  endDate: string | null;
  atRiskTier: AtRiskTier;
  isStalled: boolean;
  stalledDays: number | null;
  section: ManagerHomeSection;
  totalCount: number | null;
  completeCount: number | null;
  approvedCount: number | null;
  daysFromEnd: number | null;
  isMultiGrader?: boolean | null;
  codeTypes?: string[];
  rubricName?: string | null;
  graders?: ManagerHomeGrader[];
  completed?: boolean;
  onClick: () => void;
  onReassign?: () => void;
  onArchive?: () => void;
  onMarkComplete?: () => void;
}

interface CodeTypeConfig {
  label: string;
  bg: string;
  color: string;
}

const CODE_TYPE_CONFIG: Record<string, CodeTypeConfig> = {
  procedure: { label: 'CPT', bg: '#e0fcf0', color: '#26835b' },
  cpt: { label: 'CPT', bg: '#e0fcf0', color: '#26835b' },
  diagnosis: { label: 'ICD', bg: '#f3eaf2', color: '#875882' },
  icd: { label: 'ICD', bg: '#f3eaf2', color: '#875882' },
  modifier: { label: 'MOD', bg: '#eaf2ff', color: '#186ae1' },
  mod: { label: 'MOD', bg: '#eaf2ff', color: '#186ae1' },
};

function getCodeTypeConfig(ct: string): CodeTypeConfig {
  return CODE_TYPE_CONFIG[ct.toLowerCase()] ?? { label: ct, bg: '#edf2f7', color: '#4a5568' };
}

function formatGraderName(g: ManagerHomeGrader): string {
  const first = g.firstName ?? '';
  const lastInitial = g.lastName ? `${g.lastName[0]}.` : '';
  return [first, lastInitial].filter(Boolean).join(' ');
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  rank,
  projectName,
  groupLabel,
  purpose,
  endDate,
  atRiskTier,
  isStalled,
  stalledDays,
  section,
  totalCount,
  completeCount,
  approvedCount,
  daysFromEnd,
  isMultiGrader,
  codeTypes = [],
  rubricName,
  graders = [],
  completed = false,
  onClick,
  onReassign,
  onArchive,
  onMarkComplete,
}) => {
  const isCompleted = completed || section === 'completed';
  const stats = buildStats(section, totalCount, completeCount, approvedCount, daysFromEnd, atRiskTier);

  const fullDate = formatCardDate(endDate);
  // Completed projects never show overdue / at-risk indicators.
  const showRiskIndicators = !isCompleted;
  const dueDateText = fullDate
    ? `${showRiskIndicators && atRiskTier === 'overdue' ? '⚠ ' : ''}Target completion: ${fullDate}`
    : 'No due date';

  const cardClass = [
    'cmx-project-card',
    showRiskIndicators && atRiskTier === 'overdue' && 'cmx-project-card--overdue',
    showRiskIndicators && atRiskTier === 'due-soon' && 'cmx-project-card--due-soon',
  ]
    .filter(Boolean)
    .join(' ');

  const statusBadgeStatus = RISK_TIER_TO_STATUS[atRiskTier];

  return (
    <div className={cardClass} onClick={onClick} data-testid="manager-home-card">
      {/* Row 1: priority, title, badge, CTAs, then stats far right */}
      <Flex align="center" gap="3" width="100%">
        <PriorityBadge position={rank} />

        <Text
          fontWeight={600}
          fontSize="15px"
          noOfLines={1}
          title={projectName}
          flexShrink={0}
        >
          {projectName}
        </Text>

        {/* Status badge — immediately after name */}
        {isCompleted ? (
          <Tag
            variant="unstyled"
            className="cmx-project-card__completed-badge"
            data-testid="completed-badge"
          >
            Completed
          </Tag>
        ) : isStalled ? (
          <Tag
            variant="unstyled"
            className="cmx-project-card__stalled-badge"
            data-testid="stalled-badge"
          >
            {stalledDays !== null ? `Stalled for ${stalledDays} days` : 'Stalled'}
          </Tag>
        ) : statusBadgeStatus ? (
          <StatusBadge status={statusBadgeStatus} />
        ) : null}

        {/* Primary CTA */}
        <Button
          size="sm"
          bg="#e5927d"
          color="white"
          _hover={{ bg: '#c37c6a' }}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            if (section === 'needs_assignment' && onReassign) {
              onReassign();
            } else {
              onClick();
            }
          }}
        >
          {SECTION_PRIMARY_CTA[section]}
        </Button>

        {/* Re-assign button — in_flight only */}
        {section === 'in_flight' && onReassign && (
          <Button
            size="sm"
            variant="outline"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onReassign();
            }}
            data-testid="reassign-button"
          >
            Re-assign
          </Button>
        )}

        {/* Archive button — coder_complete only */}
        {section === 'coder_complete' && onArchive && (
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onArchive();
            }}
            data-testid="archive-button"
          >
            Archive
          </Button>
        )}

        {/* Mark Complete button — active projects only, not when already completed */}
        {!isCompleted &&
          (section === 'in_flight' || section === 'coder_complete') &&
          onMarkComplete && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onMarkComplete();
              }}
              data-testid="mark-complete-button"
            >
              Mark Complete
            </Button>
          )}

        {/* Stats pushed to far right */}
        <Flex gap="6" ml="auto" flexShrink={0}>
          {stats.map((stat, i) => (
            <QueueStat
              key={i}
              value={stat.value}
              label={stat.label}
              colorOverride={stat.colorOverride}
            />
          ))}
        </Flex>
      </Flex>

      {/* Row 2: coder info (left) + metadata (right) */}
      <Flex align="center" mt="3" pl="40px" className="cmx-project-card__meta">
        {/* Left: multi-grader badge first, then coder breakdown (in_flight) or count (other sections) */}
        <Flex gap="6" align="center" flexWrap="wrap">
          {isMultiGrader && (
            <Tag
              variant="unstyled"
              className="cmx-project-card__multi-grader-badge"
              data-testid="multi-grader-badge"
            >
              Multi-grader
            </Tag>
          )}
          {section === 'in_flight' && graders.length > 0 ? (
            <Flex align="center" gap="1" flexWrap="wrap">
              <BiomeIcon iconName={IconName.User} titleText="Coders" />
              {graders.map((g: ManagerHomeGrader) => (
                <Text
                  key={g.id}
                  fontSize="sm"
                  color="var(--color-text-medium-emphasis)"
                  className="cmx-project-card__grader-entry"
                  mr="3"
                >
                  {formatGraderName(g)}
                  {g.completedCount != null && g.totalCount != null
                    ? ` ${g.completedCount}/${g.totalCount}`
                    : g.completedCount != null
                    ? ` ${g.completedCount} done`
                    : null}
                </Text>
              ))}
            </Flex>
          ) : null}
        </Flex>

        {/* Right: group / purpose / target date */}
        <Flex ml="auto" gap="6" align="center" flexWrap="wrap">
          <span className="cmx-project-card__group-pill">{groupLabel}</span>

          {purpose && (
            <>
              <span className="cmx-project-card__meta-separator">·</span>
              <span>{purpose}</span>
            </>
          )}

          <span className="cmx-project-card__meta-separator">·</span>

          <span
            className={
              showRiskIndicators && atRiskTier === 'overdue'
                ? 'cmx-project-card__due-text--overdue'
                : showRiskIndicators && atRiskTier === 'due-soon'
                ? 'cmx-project-card__due-text--due-soon'
                : undefined
            }
          >
            {dueDateText}
          </span>
        </Flex>
      </Flex>

      {/* Row 3: task details — code types + rubric name */}
      {(codeTypes.length > 0 || rubricName) && (
        <Flex
          align="center"
          mt="2"
          pl="40px"
          gap="2"
          flexWrap="wrap"
          className="cmx-project-card__task-details"
        >
          {codeTypes.map((ct) => {
            const config = getCodeTypeConfig(ct);
            return (
              <Tag
                key={ct}
                size="sm"
                variant="unstyled"
                className="cmx-project-card__code-type-tag"
                data-testid={`code-type-tag-${ct.toLowerCase()}`}
                px="8px"
                py="2px"
                borderRadius="9999px"
                bg={config.bg}
                color={config.color}
                fontWeight={600}
                fontSize="xs"
              >
                {config.label}
              </Tag>
            );
          })}
          {rubricName && (
            <Text
              fontSize="xs"
              color="var(--color-text-medium-emphasis)"
              className="cmx-project-card__rubric-name"
            >
              {codeTypes.length > 0 && (
                <span className="cmx-project-card__meta-separator" style={{ marginRight: '6px' }}>·</span>
              )}
              {rubricName}
            </Text>
          )}
        </Flex>
      )}
    </div>
  );
};

ProjectCard.displayName = 'ProjectCard';
export default ProjectCard;
